import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as searchActions } from "../../store/actions.js";
import { getSearchbox } from "../../store/selectors.js";
import { SBContext } from "../../component-controllers/SearchboxController.js";
import useValidation from "./useValidation.js";
export default function useSearchBox() {
    const oldContext = useContext(SBContext);
    const searchbox = useSelector(getSearchbox);
    const dispatch = useDispatch();
    const { validate, focusFirstInvalidInput } = useValidation(!!oldContext.allowEmptyDestination);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const cabinClass = useMemo(() => oldContext.cabinClass, [oldContext.cabinClass]);
    const setCabinClass = useCallback((cabinClass) => {
        dispatch(searchActions.setCabinClass(cabinClass));
    }, [dispatch]);
    const occupancy = useMemo(() => ({
        adults: oldContext.adults,
        children: oldContext.children
    }), [oldContext.adults, oldContext.children]);
    const setOccupancy = useCallback((value) => {
        dispatch(searchActions.setOccupancy(value));
    }, [dispatch]);
    const segments = useMemo(() => oldContext.searchSegments, [oldContext.searchSegments]);
    const setSegmentTo = useCallback((locations, idx) => {
        dispatch(searchActions.setTo(locations, idx));
    }, [dispatch]);
    const setSegmentFrom = useCallback((locations, idx) => {
        dispatch(searchActions.setFrom(locations, idx));
    }, [dispatch]);
    const removeSegment = useCallback((idx) => {
        dispatch(searchActions.removeSegment(idx));
    }, [dispatch]);
    const swapSegment = useCallback((idx) => {
        dispatch(searchActions.swapFromTo(idx));
    }, [dispatch]);
    const addSegment = useCallback(() => {
        dispatch(searchActions.addSegment());
    }, [dispatch]);
    const clearSegments = useCallback(() => {
        dispatch(searchActions.clearSegments());
    }, [dispatch]);
    const setIsDirect = useCallback((isDirect) => {
        dispatch(searchActions.setIsDirect(isDirect));
    }, [dispatch]);
    const isDirect = useMemo(() => {
        return searchbox.isDirect;
    }, [searchbox.isDirect]);
    const setDate = useCallback((range, segmentIndex) => {
        dispatch(searchActions.setDateRange(range, segmentIndex));
    }, [dispatch]);
    const searchType = useMemo(() => oldContext.type, [oldContext.type]);
    const cleanupBasedOnSearchType = useCallback((searchType) => {
        if (searchType === "MULTISTOP") {
            const segment0 = segments?.[0];
            if (segment0?.from?.length > 1 || segment0?.to?.length > 1) {
                const from = segment0?.from?.[0];
                const to = segment0?.to?.[0];
                setSegmentFrom(from ? [from] : [], 0);
                setSegmentTo(to ? [to] : [], 0);
            }
            if (segments.length === 1 && !oldContext?.config?.hideMultiCityEmptyInputs) {
                addSegment();
            }
        }
        else if (!searchbox.initial) {
            clearSegments();
        }
    }, [
        addSegment,
        clearSegments,
        oldContext?.config?.hideMultiCityEmptyInputs,
        searchbox.initial,
        segments,
        setSegmentFrom,
        setSegmentTo
    ]);
    const setSearchType = useCallback((searchType) => {
        setIsSubmitted(false);
        dispatch(searchActions.setType(searchType));
        if (window.location.pathname.startsWith("/fly-anywhere")) {
            if (searchType === "MULTISTOP") {
                dispatch(searchActions.setTo([], 0));
            }
        }
        cleanupBasedOnSearchType(searchType);
    }, [dispatch, cleanupBasedOnSearchType]);
    const doSearch = useCallback(() => {
        setIsSubmitted(true);
        if (validate(segments, occupancy, searchType)) {
            setIsValid(true);
            oldContext.doSearch();
        }
        else {
            setIsValid(false);
            focusFirstInvalidInput();
        }
    }, [oldContext, validate, focusFirstInvalidInput, segments, occupancy, searchType]);
    const onChange = useCallback(() => {
        oldContext.onChange?.(oldContext.searchboxState);
    }, [oldContext]);
    useEffect(() => {
        setIsValid(validate(segments, occupancy, searchType));
    }, [searchType, segments, occupancy]);
    return {
        i18n: oldContext.i18n,
        autoCompleteBaseURL: oldContext.searchAutoCompleteBaseURL,
        getOptionalHeaders: oldContext.getOptionalHeaders,
        lang: oldContext.lang,
        trackV2: oldContext.trackV2,
        dateFNSLocale: oldContext.dateFNSLocale,
        doSearch,
        onChange,
        isSubmitted,
        ipCountry: oldContext.ipCountry,
        searchType,
        setSearchType,
        cabinClass,
        setCabinClass,
        occupancy,
        setOccupancy,
        segments,
        setSegmentTo,
        setSegmentFrom,
        swapSegment,
        removeSegment,
        addSegment,
        setIsDirect,
        isDirect,
        setDate,
        isValid,
        trackLandingGoal: oldContext.trackLandingGoal,
        onTrackSBExperimentCustomGoal: oldContext.onTrackSBExperimentCustomGoal,
        onTrackSBExperimentGoalWithValue: oldContext.onTrackSBExperimentGoalWithValue,
        onTrackSBExperimentStage: oldContext.onTrackSBExperimentStage,
        isHeaderFramed: !!oldContext.isHeaderFramed,
        hideShadow: !!oldContext.hideShadow,
        isMobile: !!oldContext.isMobile,
        isMeta: !!oldContext.isMeta,
        onGetSBExperimentVariant: oldContext.onGetSBExperimentVariant,
        onTrackSBExperiment: oldContext.onTrackSBExperiment,
        onTrackSBMetaExperimentStage: oldContext.onTrackSBMetaExperimentStage,
        disableAutocompleteMultiSelect: !!oldContext.hideLocationMultiSelect,
        isHeaderUpdateExp: !!oldContext.isHeaderUpdateExp,
        isHeaderOnDarkBg: !!oldContext.isHeaderOnDarkBg,
        normalizeHeaderPadding: !!oldContext.normalizeHeaderPadding,
        reorderControls: !!oldContext.reorderControls,
        isResponsiveFixes: !!oldContext.isResponsiveFixes,
        showNonStopExp: !!oldContext.showNonStopExp,
        formatPrice: oldContext.priceFormatter,
        routeName: oldContext.routeName,
        multiSelectVariant: oldContext.multiSelectVariant,
        multiSelectName: oldContext.multiSelectName,
        infantMaxAge: oldContext.infantMaxAge,
        fixInputSpacing: oldContext.fixInputSpacing,
        isDateDropdownOpen: oldContext.isDateDropdownOpen,
        enabledCountriesAndAnywhere: !!oldContext.enabledCountriesAndAnywhere,
        allowEmptyDestination: !!oldContext.allowEmptyDestination,
        isSpringDealExp: !!oldContext.isSpringDealExp,
        isSummerDealExp: !!oldContext.isSummerDealExp,
        fareCalendarConfig: oldContext.fareCalendarConfig,
        isSearchAnyCityEnabled: !!oldContext.isSearchAnyCityEnabled,
        isCalendarReflowFixEnabled: !!oldContext.isCalendarReflowFixEnabled,
        isMapZoomReflowEnabled: !!oldContext.isMapZoomReflowEnabled,
        recentSearchHistory: searchbox.recentSearchHistory,
        onRecentSearchSelect: oldContext.onRecentSearchSelect,
        showCabinClassLabel: !!oldContext.showCabinClassLabel
    };
}
